import {Component, DestroyRef, ElementRef, OnInit, afterNextRender, computed, inject, signal, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {BadgeModule} from 'primeng/badge';
import {StyleClassModule} from 'primeng/styleclass';
import {RouterOutlet} from '@angular/router';
import {
  AVAILABLE_ADMIN_ITEMS, AVAILABLE_MANAGE_ITEMS,
  BASE_ADMIN_ITEM,
  BASE_MANAGE_ITEM,
  BASE_NAV_ITEMS,
  BASE_INFO_ITEMS,
  NavItem
} from '../../navigation/navigation';
import {SideMenuItemComponent} from '../../navigation/side-menu-item.component/side-menu-item.component';
import {TaAppStore} from '../../../state/clcta-store';
import {Button, ButtonDirective} from 'primeng/button';
import {SessionDetails, UserDetails} from '../../../api';
import {BreadCrumbBarComponent} from '../../components/bread-crumb-bar/bread-crumb-bar.component';
import {PermissionService} from '../../../services/permission.service';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {Toast} from 'primeng/toast';
import {MsalService} from '@azure/msal-angular';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {Menu} from 'primeng/menu';
import {MenuItem} from 'primeng/api';
import {Tooltip} from 'primeng/tooltip';
import {QuickSearchComponent} from '../../components/quick-search/quick-search.component';
import {isMacPlatform} from '../../../util/platform.utils';
import {ChatWindow} from '../../components/chat-window/chat-window';
import {TapHelperService} from '../../components/chat-window/tap-helper.service';

@Component({
  selector: 'ta-layout',
  imports: [CommonModule, BadgeModule, StyleClassModule, RouterOutlet, SideMenuItemComponent, ButtonDirective, Button, BreadCrumbBarComponent, ConfirmDialog, Toast, Menu, Tooltip, QuickSearchComponent, ChatWindow],
  templateUrl: './ta-layout.html',
  styleUrl: './ta-layout.scss',
  host: {
    '(document:keydown)': 'onKeydown($event)'
  }
})
export class TaLayout implements OnInit {

  navItems: NavItem[] = [];
  userMenuItems: MenuItem[] = [
    { label: 'Sign out', icon: 'fa-solid fa-right-from-bracket', command: () => this.logout() }
  ];
  isSidebarOpen = signal(true);
  quickSearchOpen = signal(false);
  private readonly isMac = isMacPlatform();
  protected tapHelper = inject(TapHelperService);
  private quickSearch = viewChild(QuickSearchComponent);
  version = computed(() => this.store.session().details?.version ?? '');
  envName = computed(() => this.store.session().environmentName);
  user = computed<UserDetails | null>( () => this.store.user())
  session = computed<SessionDetails | null>(() => this.store.session()?.details);

  // Sidebar menu top fade: shown only when the menu overflows and is scrolled down.
  showMenuFade = signal(false);
  menuScrollbarWidth = signal(0);
  private menuScroller = viewChild.required<ElementRef<HTMLElement>>('menuScroller');
  private menuList = viewChild.required<ElementRef<HTMLElement>>('menuList');

  constructor(
    public store: TaAppStore,
    protected permissionService: PermissionService,
    private authService: MsalService,
    private http: HttpClient
  ) {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      this.updateMenuFade();
      // Catches group expand/collapse and window resize, which don't fire scroll events.
      const observer = new ResizeObserver(() => this.updateMenuFade());
      observer.observe(this.menuScroller().nativeElement);
      observer.observe(this.menuList().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  updateMenuFade(): void {
    const el = this.menuScroller().nativeElement;
    this.showMenuFade.set(el.scrollHeight > el.clientHeight && el.scrollTop > 0);
    this.menuScrollbarWidth.set(el.offsetWidth - el.clientWidth);
  }

  ngOnInit() {
    this.generateNavItems();
  }

  public toggleSidebar(): void {
    this.isSidebarOpen.update(v => !v);
  }

  // Ctrl+K (Windows) / ⌘K (Mac) opens Quick Search.
  onKeydown(event: KeyboardEvent): void {
    const modifier = this.isMac ? event.metaKey : event.ctrlKey;
    if (!modifier || event.altKey || event.shiftKey || event.repeat || event.key?.toLowerCase() !== 'k') {
      return;
    }
    if (!this.permissionService.canQuickSearch()) {
      return;
    }
    event.preventDefault();
    if (this.quickSearchOpen()) {
      this.quickSearch()?.focusInput();
    } else {
      this.quickSearchOpen.set(true);
    }
  }

  public hideEnvName(): void {
    this.store.hideEnvName();
  }

  getUserManual(): void {
    console.log('Getting user manual... ');
    const url = '/clcta/file/help-manual';
    window.open(url, '_blank');
  }

  logout(): void {
    this.tapHelper.clearLocal();
    this.http.post(`${environment.tenantSettings.baseUrl}/clcta/user/logout`, {}).subscribe({
      complete: () => this.authService.logoutRedirect(),
      error: () => this.authService.logoutRedirect()
    });
  }

  generateNavItems(): void {
    const tempNavItems: NavItem[] = BASE_NAV_ITEMS.map(item => ({
      ...item,
      children: item.children ? [...item.children] : []
    }));

    if (!this.permissionService.currentUser()!.isActive!){
      this.navItems = [...tempNavItems];
      return;
    }

    const manageChildren = AVAILABLE_MANAGE_ITEMS.filter(item =>
      this.permissionService.canAccessPath(item.url ?? '')
    );
    if (manageChildren.length) {
      tempNavItems.push({
        ...BASE_MANAGE_ITEM,
        children: manageChildren
      });
    }

    const adminChildren = AVAILABLE_ADMIN_ITEMS.filter(item =>
      this.permissionService.canAccessPath(item.url ?? '')
    );
    if (adminChildren.length) {
      tempNavItems.push({
        ...BASE_ADMIN_ITEM,
        children: adminChildren
      });
    }

    // info items are for everybody but should be at the bottom. TAP Helper's action needs the layout's service.
    tempNavItems.push(...BASE_INFO_ITEMS.map(group => ({
      ...group,
      children: [...group.children ?? [], {
        id: 'info-tapHelper',
        type: 'item' as const,
        title: 'TAP Assistant',
        svgIcon: 'assets/images/tap-helper-line.svg',
        action: () => this.tapHelper.openFull(),
      }],
    })));

    this.navItems = [...tempNavItems];
  }
}
